<template>
    <div class="podcast-edit">
        <Spinner v-if="loading" />
        <button
            v-if="podcast.id"
            class="button button--lightgray podcast-edit__toggle"
            type="button"
            @click="toggleEditMode"
        >
            &#x270e;
            {{ editMode ? $t('podcasts.detail.cancelEditing') : $t('podcasts.detail.editPodcast') }}
        </button>
        <form class="form podcast-edit__form podcast-form" @submit.prevent="handleSave">
            <div class="podcast-form__col podcast-form__cover">
                <PhotoInput
                    size="215"
                    no-delete
                    :photo="podcast.cover"
                    no-filename
                    @selected="handleCoverSelection"
                />
            </div>
            <div class="podcast-form__col">
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.name') }}
                        <i v-if="editMode" class="form__required">*</i>
                    </label>
                    <FormInput
                        v-if="editMode"
                        v-model="podcast.name"
                        type="text"
                        name="name"
                        :placeholder="$t('podcasts.detail.name')"
                        width="100%"
                        required
                    />
                    <p v-else>
                        {{ podcast.name }}
                    </p>
                </div>
                <div v-if="!editMode && podcast.id && podcast.feed_url" class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.rss') }}
                    </label>
                    <p>
                        <button type="button" class="podcast-edit__feed" @click="copyRssFeed">
                            {{ podcast.feed_url }}
                            <span>
                                {{ $t('podcasts.copyFeed') }}
                            </span>
                        </button>
                    </p>
                </div>
                <div v-if="podcast.id" class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.episodes') }}
                    </label>
                    <p>
                        <button
                            type="button"
                            class="button button--medium"
                            @click="showEpisodesModal = true"
                        >
                            {{ $t('podcasts.detail.editEpisodes') }}
                        </button>
                    </p>
                </div>
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.email') }}
                        <i v-if="editMode" class="form__required">*</i>
                    </label>
                    <FormInput
                        v-if="editMode"
                        v-model="podcast.email"
                        type="email"
                        name="email"
                        :placeholder="$t('podcasts.detail.email')"
                        width="100%"
                        required
                    />
                    <p v-else>
                        {{ podcast.email }}
                    </p>
                </div>
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.author') }}
                        <i v-if="editMode" class="form__required">*</i>
                    </label>
                    <FormInput
                        v-if="editMode"
                        v-model="podcast.author"
                        type="text"
                        name="author"
                        :placeholder="$t('podcasts.detail.author')"
                        width="100%"
                        required
                    />
                    <p v-else>
                        {{ podcast.author }}
                    </p>
                </div>
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.descr') }}
                        <i v-if="editMode" class="form__required">*</i>
                    </label>
                    <textarea
                        v-if="editMode"
                        v-model="podcast.description"
                        class="form-group__textarea"
                        :placeholder="$t('podcasts.detail.descr')"
                        required="true"
                    />
                    <p v-else>
                        {{ podcast.description }}
                    </p>
                </div>
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.detail.lang') }}
                        <i v-if="editMode" class="form__required">*</i>
                    </label>
                    <CustomSelect
                        v-if="editMode"
                        v-model="podcast.language"
                        emit-prop="name"
                        view-label="label"
                        :options="flatLanguages"
                        style="width: 300px"
                        required
                    />
                    <p v-else>
                        {{ podcast.language.label }}
                    </p>
                </div>
                <div class="form-group">
                    <label
                        :class="[
                            'form-group__label',
                            { 'form-group__label--error': errorRequired.category },
                        ]"
                    >
                        {{ $t('podcasts.detail.category') }}
                        <i v-if="editMode" class="form__required">*</i>
                    </label>
                    <p>
                        {{ podcast.categories.value || podcast.categories.label }}
                    </p>
                    <PodcastCategorySelector
                        v-if="editMode"
                        v-model="podcast.categories"
                        :categories="categoriesTree"
                    />
                </div>
                <div class="form-group">
                    <Checkbox v-model="podcast.is_explicit" theme="inverted" :disabled="!editMode">
                        {{ $t('podcasts.detail.isExplicit') }}
                    </Checkbox>
                </div>
                <div class="form-group">
                    <Checkbox v-model="podcast.is_active" theme="inverted" :disabled="!editMode">
                        {{ $t('podcasts.detail.isActive') }}
                    </Checkbox>
                </div>
                <div class="form-group podcast-form__actions">
                    <button
                        v-if="editMode"
                        type="submit"
                        class="button button--big podcast-form__save"
                        :disabled="loading"
                    >
                        {{ $t('podcasts.detail.save') }}
                    </button>
                    <PodcastDeleteModal
                        v-if="podcast.id"
                        :podcast="podcast"
                        @deleted="handleDelete"
                    >
                        {{ $t('podcasts.detail.delete') }}
                    </PodcastDeleteModal>
                </div>
                <ErrorList :error="error" />
            </div>
        </form>
        <portal v-if="showSuccessModal" to="modals">
            <Modal
                name="podcast-added"
                :title="$t('podcasts.detail.success')"
                @closed="handleCloseSuccessModal"
            >
                <template #content>
                    <div class="podcast-edit__success">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512.736 512.736"
                            width="140"
                            height="140"
                        >
                            <path
                                d="M238 512.736c0-76.8-128.544-205.344-205.344-205.344 76.8 0 205.344-128.544 205.344-205.344 0 76.8 128.544 205.344 205.344 205.344-76.8 0-205.344 128.544-205.344 205.344z"
                                fill="#ffd83b"
                            />
                            <path
                                d="M238 388.272c0-30.24-50.64-80.88-80.88-80.88 30.24 0 80.88-50.64 80.88-80.88 0 30.24 50.64 80.88 80.88 80.88-30.256 0-80.88 50.624-80.88 80.88z"
                                fill="#f3c432"
                            />
                            <g fill="#ffd83b">
                                <path
                                    d="M381.168 226.512c0-36.992-61.92-98.912-98.912-98.912 36.992 0 98.912-61.92 98.912-98.912 0 36.992 61.92 98.912 98.912 98.912-36.992 0-98.912 61.92-98.912 98.912zM92.976 505.36c0-19.056-31.888-50.944-50.944-50.944 19.056 0 50.944-31.888 50.944-50.944 0 19.056 31.888 50.944 50.944 50.944-19.056.016-50.944 31.904-50.944 50.944zM158.688 141.408c0-26.448-44.256-70.704-70.704-70.704 26.448 0 70.704-44.256 70.704-70.704 0 26.448 44.256 70.704 70.704 70.704-26.448 0-70.704 44.256-70.704 70.704z"
                                />
                            </g>
                        </svg>
                        <p>
                            {{ $t('podcasts.detail.created1') }}
                            <a class="link" @click.prevent="toEpisodes">
                                {{ $t('podcasts.detail.addLink') }}
                            </a>
                            {{ $t('podcasts.detail.created2') }}
                        </p>
                    </div>
                </template>
            </Modal>
        </portal>
        <PodcastEpisodesEditor
            v-if="showEpisodesModal"
            :podcast="podcast"
            @closed="onEpisodesEditorClosed"
        />
    </div>
</template>

<script>
import {
    PhotoInput,
    FormInput,
    Checkbox,
    CustomSelect,
    ErrorList,
} from '@speechki-frontend/shared/ui/forms';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { copyToClipboard } from '@speechki-frontend/shared/helpers/functions';
import PodcastDeleteModal from '../PodcastDeleteModal/PodcastDeleteModal';
import PodcastEpisodesEditor from '../PodcastEpisodesEditor/PodcastEpisodesEditor';
import PodcastCategorySelector from '../PodcastCategorySelector/PodcastCategorySelector';

function getDeepest(obj) {
    let target = obj;
    if (obj.children && obj.children.length) {
        obj.children.forEach((child) => {
            target = child;
            if (child.children && child.children.length) {
                target = getDeepest(child);
            }
        });
    }
    return target;
}

export default {
    name: 'PodcastEdit',
    components: {
        PhotoInput,
        FormInput,
        Checkbox,
        CustomSelect,
        ErrorList,
        PodcastDeleteModal,
        Modal,
        PodcastEpisodesEditor,
        PodcastCategorySelector,
    },
    data() {
        return {
            podcast: {
                id: '',
                name: '',
                email: '',
                author: '',
                description: '',
                is_explicit: false,
                is_active: true,
                feed_url: '',
                categories: {
                    name: '',
                },
                language: {
                    name: this.$i18n.locale == 'rus' ? 'ru' : 'en',
                    label: this.$i18n.locale == 'rus' ? 'Русский' : 'English',
                },
                cover: '',
            },
            categories: [],
            languages: [],
            loading: false,
            editMode: false,
            error: null,
            errorRequired: { category: false },
            showSuccessModal: false,
            showEpisodesModal: false,
        };
    },
    computed: {
        flatLanguages() {
            return this.languages.flat();
        },
        isRequiredValid() {
            return Object.values(this.errorRequired).every((e) => !e);
        },

        categoriesTree() {
            if (!this.categories.length) return [];
            return this.makeTree(this.categories);
        },
    },
    watch: {
        editMode() {
            if (this.editMode) {
                this.podcast.language = this.podcast.language.name;
            }
        },
    },
    async created() {
        this.loading = true;
        await Promise.all([this.fetchCategories(), this.fetchLanguages()]);

        if (this.$route.params.id) {
            await this.fetchPodcast(this.$route.params.id);
        } else {
            this.editMode = true;
            this.loading = false;
        }
    },
    methods: {
        async fetchPodcast(id) {
            try {
                this.loading = true;
                const podcast = await this.$http.get(`/api/v1/podcast/podcasts/${id}/`);
                podcast.categories = getDeepest(podcast.categories);
                this.podcast = podcast;
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        async fetchCategories() {
            try {
                this.categories = await this.$http.get(
                    '/api/v1/podcast/podcasts/categories/?page_size=999'
                );
            } catch (e) {
                console.log(e);
            }
        },
        async fetchLanguages() {
            try {
                const results = await this.$http.get(
                    '/api/v1/podcast/podcasts/languages/?page_size=999'
                );

                this.languages = results.flat();
            } catch (e) {
                console.log(e);
            }
        },
        async handleSave() {
            try {
                if (this.validateRequired()) {
                    this.loading = true;
                    if (this.podcast.id) {
                        await this.updatePodcast();
                        await this.fetchPodcast(this.podcast.id);
                        this.editMode = false;
                    } else {
                        const { id } = await this.createPodcast();
                        this.$router.push(`/podcasts/${id}`);
                        await this.fetchPodcast(id);
                        this.editMode = false;
                        this.showSuccessModal = true;
                    }
                }
            } catch (e) {
                this.error = e;
            } finally {
                this.loading = false;
            }
        },
        handleCoverSelection(file) {
            this.podcast.cover = file;
        },
        async updatePodcast() {
            const data = this.prepareData();

            return await this.$http.patch(`/api/v1/podcast/podcasts/${this.podcast.id}/`, data);
        },
        async createPodcast() {
            const data = this.prepareData();

            return await this.$http.post('/api/v1/podcast/podcasts/', data);
        },
        prepareData() {
            const data = new FormData();

            Object.entries(this.podcast).forEach(([key, value]) => {
                if (key === 'cover' && (!value || typeof value === 'string')) {
                    return;
                }

                if (key === 'categories') {
                    data.append('category', value.name);
                    return;
                }

                data.append(key, value);
            });

            return data;
        },
        handleDelete() {
            this.$router.push('/podcasts');
        },
        toggleEditMode() {
            this.editMode = !this.editMode;

            if (!this.editMode) {
                this.fetchPodcast(this.podcast.id);
            }
        },
        copyRssFeed() {
            copyToClipboard(this.podcast.feed_url);
            this.$store.dispatch('pushToast', {
                id: Date.now(),
                type: 'success',
                title: this.$t('podcasts.copyFeedSuccess'),
            });
        },
        handleCloseSuccessModal() {
            this.$router.push('/podcasts');
        },
        toEpisodes() {
            this.showSuccessModal = false;
            this.showEpisodesModal = true;
        },
        onEpisodesEditorClosed() {
            this.showEpisodesModal = false;
        },
        validateRequired() {
            const { categories } = this.podcast;
            this.errorRequired.category = !categories.name;

            return this.isRequiredValid;
        },
        makeTree(categories, nesting = 0) {
            const vm = this;
            nesting++;
            return categories.map((category) => {
                const { name, label, children: childs = [] } = category;

                const children = vm.makeTree(childs, nesting);
                const childActive = children.length
                    ? children.some((child) => child.active)
                    : false;

                return {
                    name,
                    label,
                    nesting,
                    childActive,
                    children,
                    get active() {
                        const { name: selected = null } = vm.podcast.categories;
                        return selected === name;
                    },
                };
            });
        },
    },
};
</script>

<style lang="scss" src="./PodcastEdit.scss"></style>
